<script setup lang="ts">
import {BlockProps} from "@/utils/presenters/interfaces.ts";
import {NamaNavigationArgs, NamaVueWidget} from "@/utils/common/NamaVueWidget.ts";
import {computed, onBeforeMount} from "vue";
import Translator from "@/utils/common/Translator.ts";
import {storeToRefs} from "pinia";
import {useNamaFlagsStore} from "@/stores/nama-flags-store.ts";
import QuestionFieldsEditor from "@/components/questionFields/QuestionFieldsEditor.vue";
import {QuestionFieldsPresenter} from "@/utils/presenters/QuestionFieldsPresenter.ts";
import LoginUtils from "@/utils/login/LoginUtils.ts";
import DomainBaseFieldIDs from "@/utils/constants/DomainBaseFieldIDs.ts";
import NamaCommon from "@/utils/common/NamaCommon.ts";
import NamaURLS from "@/utils/constants/NamaURLS.ts";
import {useMessagesStore} from "@/stores/ToastDataStore.ts";
import EditViewUtils from "@/utils/editView/EditViewUtils.ts";
import EditScreenBlock = namaTypes.EditScreenBlock;
import NaMaMetaData = namaTypes.NaMaMetaData;
import QuestionField = namaTypes.QuestionField;
import DTOFilledQuestionField = namaTypes.DTOFilledQuestionField;
import DTODiscussionRecord = namaTypes.DTODiscussionRecord;

const props = defineProps<BlockProps>();
const emit = defineEmits<{
  (e: 'navigateToNext', args: NamaNavigationArgs): void;
  (e: 'discussionAdded', args: NamaVueWidget): void;
}>();
const blockRef = computed<EditScreenBlock>(() => props.block as EditScreenBlock);
const {compactMode} = storeToRefs(useNamaFlagsStore());
const metadata: NaMaMetaData | undefined = props.presenter.metadata;
const ownerId: string | undefined = metadata?.owner?.ownerId;
const prefix = 'discussion.';

let questionFieldPresenter: QuestionFieldsPresenter = new QuestionFieldsPresenter(ownerId);
let questionFields: QuestionField[] = EditViewUtils.createDiscussionFields(ownerId ?? '', prefix, props.presenter.layout);
onBeforeMount(async () => {
  await questionFieldPresenter.edit(questionFields ?? [], []);
})

async function onAddClick() {
  let filledQuestions: DTOFilledQuestionField[] = questionFieldPresenter.getQuestionFields();
  let req: DTODiscussionRecord = {
    owner: props.presenter.fetchRecord(),
    discussion: filledQuestions.find(l => l.id === prefix + DomainBaseFieldIDs.DiscussionRecord_discussion)?.value,
    user: LoginUtils.loginInfo?.userInfo,
    ref1: filledQuestions.find(l => l.id === prefix + DomainBaseFieldIDs.DiscussionRecord_ref1)?.objValue,
    ref2: filledQuestions.find(l => l.id === prefix + DomainBaseFieldIDs.DiscussionRecord_ref2)?.objValue,
    attachment1: filledQuestions.find(l => l.id === prefix + DomainBaseFieldIDs.DiscussionRecord_attachment1)?.objValue,
    attachment2: filledQuestions.find(l => l.id === prefix + DomainBaseFieldIDs.DiscussionRecord_attachment2)?.objValue,
    attachment3: filledQuestions.find(l => l.id === prefix + DomainBaseFieldIDs.DiscussionRecord_attachment3)?.objValue,
    attachment4: filledQuestions.find(l => l.id === prefix + DomainBaseFieldIDs.DiscussionRecord_attachment4)?.objValue,
    date1: filledQuestions.find(l => l.id === prefix + DomainBaseFieldIDs.DiscussionRecord_date1)?.objValue,
    date2: filledQuestions.find(l => l.id === prefix + DomainBaseFieldIDs.DiscussionRecord_date2)?.objValue,
    time1: filledQuestions.find(l => l.id === prefix + DomainBaseFieldIDs.DiscussionRecord_time1)?.objValue,
    time2: filledQuestions.find(l => l.id === prefix + DomainBaseFieldIDs.DiscussionRecord_time2)?.objValue,
  };
  let response = await NamaCommon.namaFetch(NamaURLS.AddDiscussion, {data: req});
  if (response && response.result && response.result.succeded) {
    useMessagesStore().pushMessageStr(Translator.translateId("recordSavedCorrectly"));
    emit('discussionAdded', {});
    questionFieldPresenter.record.value = {};
  }
}

</script>

<template>
  <q-card class="tw-h-full tw-w-full tw-flex tw-flex-col tw-shadow-none tw-bg-transparent tw-p-0">
    <q-card-section
      :dir="Translator.fetchDir()"
      class="tw-shrink tw-text-primary-light dark:tw-text-primary-dark tw-text-base tw-font-normal tw-h-full">
      <QuestionFieldsEditor :presenter="questionFieldPresenter" :doNotApplyThemeLayouts="false"
                            @navigateToNext="(args)=>$emit('navigateToNext', args)"/>
    </q-card-section>
    <q-card-section :dir="Translator.fetchDir()" class="tw-flex-none">
      <div class="tw-flex tw-flex-row tw-gap-4 tw-justify-center ">
        <q-btn
          :label="Translator.translateId('add')"
          @click="onAddClick()"
          :dense="compactMode"
          class="tw-bg-primary-light dark:tw-bg-primary-dark tw-text-white tw-text-base tw-font-normal tw-rounded-md tw-min-w-28 tw-max-w-fit"/>
      </div>
    </q-card-section>
  </q-card>
</template>

<style scoped>

</style>
